import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '../components/Breadcrumb';
import ShareButtons from '../components/ShareButtons';
import BuyButtons from '../components/BuyButtons';

// Helper to ensure array
const toArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') return val.split('\n').map(s => s.trim()).filter(Boolean);
  return [];
};

const ReviewPage = () => {
  const { title } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch('/.netlify/functions/get-products');
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          const foundProduct = data.data.find(p => p.title === decodeURIComponent(title));
          if (foundProduct) {
            setProduct(foundProduct);
          } else {
            setError('Product not found');
          }
        } else {
          setError('Invalid data format');
        }
      } catch (err) {
        setError('Error fetching product');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [title]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">Loading review...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-500">{error || 'Product not found'}</div>
      </div>
    );
  }

  const keyFeatures = toArray(product.key_features);
  const pros = toArray(product.pros);
  const cons = toArray(product.cons);

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-8">
      <Helmet>
        <title>{product.title} Review, Features, Pros & Cons, FAQs | The Tech Nerd</title>
        <meta name="description" content={product.description || `Read our review of ${product.title} on The Tech Nerd.`} />
        <meta property="og:title" content={`${product.title} Review, Features, Pros & Cons, FAQs | The Tech Nerd`} />
        <meta property="og:description" content={product.description || `Read our review of ${product.title} on The Tech Nerd.`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://thetechnerd.co/reviews/${encodeURIComponent(product.title)}`} />
        <link rel="canonical" href={`https://thetechnerd.co/reviews/${encodeURIComponent(product.title)}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.title} Review, Features, Pros & Cons, FAQs | The Tech Nerd`} />
        <meta name="twitter:description" content={product.description || `Read our review of ${product.title} on The Tech Nerd.`} />
        <meta name="twitter:image" content={product.image} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Review",
            "name": product.title,
            "author": {
              "@type": "Organization",
              "name": "The Tech Nerd"
            },
            "reviewBody": product.description,
            "itemReviewed": {
              "@type": "Product",
              "name": product.title,
              "image": product.image,
              "category": product.category
            },
            "url": `https://thetechnerd.co/reviews/${encodeURIComponent(product.title)}`,
            "datePublished": new Date().toISOString().split('T')[0]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `What are the key features of ${product.title}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": keyFeatures.length > 0 
                    ? keyFeatures.slice(0, 3).join(', ') + (keyFeatures.length > 3 ? ' and more.' : '.')
                    : `The ${product.title} offers several key features that make it stand out in its category.`
                }
              },
              {
                "@type": "Question",
                "name": `What are the main advantages of ${product.title}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": pros.length > 0 
                    ? pros.slice(0, 3).join(', ') + (pros.length > 3 ? ' and more.' : '.')
                    : `The ${product.title} offers several advantages that make it a great choice.`
                }
              },
              {
                "@type": "Question",
                "name": "Are there any drawbacks to consider?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": cons.length > 0 
                    ? cons.slice(0, 3).join(', ') + (cons.length > 3 ? ' and more.' : '.')
                    : `While the ${product.title} has many benefits, there are a few considerations to keep in mind.`
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <Breadcrumb category={product.category} productTitle={product.title} />
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8">
        {/* Product Header - Responsive Layout */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6 sm:mb-8">
          <div className="flex flex-col md:flex-row gap-4 sm:gap-8 p-3 sm:p-8">
            {/* Mobile: Title/Category above image, Desktop: Side by side */}
            <div className="flex flex-col w-full md:w-2/3 order-2 md:order-1 justify-center">
              <span className="inline-block px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 mb-2 sm:mb-4 w-fit">
                {product.category}
              </span>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-4" dangerouslySetInnerHTML={{ __html: product.title }} />
              {product.description && (
                <p className="text-gray-600 mb-3 sm:mb-6 text-sm sm:text-base">{product.description}</p>
              )}
              <BuyButtons productTitle={product.title} link={product.link} links={product.links} layout="stack" size="md" />
              <div className="mt-4">
                <ShareButtons 
                  title={product.title}
                  url={`https://thetechnerd.co/reviews/${encodeURIComponent(product.title)}`}
                />
              </div>
            </div>
            <div className="flex-shrink-0 w-full md:w-1/3 flex items-center justify-center order-1 md:order-2">
              <div className="w-full max-w-xs sm:max-w-sm md:max-w-full aspect-[4/3] sm:aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center mx-auto">
                <img
                  src={product.image}
                  alt={product.title}
                  loading="lazy"
                  decoding="async"
                  className="w-auto h-32 sm:h-48 md:h-full max-h-40 sm:max-h-60 md:max-h-80 object-contain mx-auto"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/300x300?text=${encodeURIComponent(product.title)}`;
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Review Content */}
        <div className="space-y-6 sm:space-y-8">
          {/* Key Features */}
          {keyFeatures.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4 sm:p-8">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Key Features</h2>
                <ul className="space-y-2">
                  {keyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 h-5 w-5 text-indigo-600">•</span>
                      <span className="ml-2 text-gray-600 text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Pros and Cons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {pros.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 sm:p-8">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Pros</h2>
                  <ul className="space-y-2">
                    {pros.map((pro, index) => (
                      <li key={index} className="flex items-start">
                        <span className="flex-shrink-0 h-5 w-5 text-green-600">✓</span>
                        <span className="ml-2 text-gray-600 text-sm sm:text-base">{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {cons.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 sm:p-8">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Cons</h2>
                  <ul className="space-y-2">
                    {cons.map((con, index) => (
                      <li key={index} className="flex items-start">
                        <span className="flex-shrink-0 h-5 w-5 text-red-600">×</span>
                        <span className="ml-2 text-gray-600 text-sm sm:text-base">{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Final Verdict */}
          {product.verdict && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4 sm:p-8">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Final Verdict</h2>
                <p className="text-gray-600 text-sm sm:text-base">{product.verdict}</p>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 sm:p-8">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">What are the key features of {product.title}?</h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {keyFeatures.length > 0 
                      ? keyFeatures.slice(0, 3).join(', ') + (keyFeatures.length > 3 ? ' and more.' : '.')
                      : `The ${product.title} offers several key features that make it stand out in its category.`}
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">What are the main advantages of {product.title}?</h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {pros.length > 0 
                      ? pros.slice(0, 3).join(', ') + (pros.length > 3 ? ' and more.' : '.')
                      : `The ${product.title} offers several advantages that make it a great choice.`}
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Are there any drawbacks to consider?</h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    {cons.length > 0 
                      ? cons.slice(0, 3).join(', ') + (cons.length > 3 ? ' and more.' : '.')
                      : `While the ${product.title} has many benefits, there are a few considerations to keep in mind.`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-indigo-50 rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 sm:p-8 text-center">
              <h2 className="text-lg sm:text-xl font-bold text-indigo-900 mb-3 sm:mb-4">Ready to Get Yours?</h2>
              <p className="text-indigo-700 mb-4 sm:mb-6 text-sm sm:text-base">Check out the latest price and availability</p>
              <div className="flex justify-center">
                <BuyButtons productTitle={product.title} link={product.link} links={product.links} layout="inline" size="lg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage; 